// Libraries
import { FC, useState, ChangeEvent, useEffect } from "react";
import { useSelector } from "react-redux";

// Store
import { AppState } from "store/reducers/Reducer";

// Hooks
import { useDebounce } from "hooks/useDebounce";
import { useWorkspaceSettings } from "hooks/useWorkspace/useWorkspaceSettings";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace/useWorkspaceIdentifiers";

// Components
import { PDFLayout } from "../PDFLayout";
import { Modal, ModalBody, ModalHeader } from "components/common/modal";
import { Divider } from "components/common/divider/Divider";
import { Switch } from "components/common/switch";
import { InputV2 } from "components/common/InputV2/InputV2";
import { Button } from "components/common/buttons/Button";

// Types
import { PDFExportProps } from "./PDFExport.types";
import { PDFLayoutProps } from "../PDFLayout/PDFLayout.types";
import { buildExportFilename, sanitizeExportProjectName, sanitizeExportSegment } from "utils";

const PDFExportInitialValues: Omit<PDFLayoutProps, "watermark" | "projectName" | "children"> = {
  companyName: "",
  address: "",
  phone: "",
  email: "",
  contact: "",
  showDate: true,
  orientation: "portrait",
  showPageNumber: true,
  showWatermark: false,
};

export const PDFExport: FC<PDFExportProps> = ({
  isOpen = false,
  onClose,
  title: _title,
  children,
  onDownload,
}) => {
  const project = useSelector((state: AppState) => state.project);
  const { workspaceId } = useWorkspaceIdentifiers();
  const { workspace } = useWorkspaceSettings(workspaceId || "");

  // Header
  const [projectName, setProjectName] = useDebounce("", 325);
  const [title, setTitle] = useDebounce("", 325);

  // Contact Details
  const [companyName, setCompanyName] = useDebounce(PDFExportInitialValues.companyName, 325);
  const [address, setAddress] = useDebounce(PDFExportInitialValues.address, 325);
  const [phone, setPhone] = useDebounce(PDFExportInitialValues.phone, 325);
  const [email, setEmail] = useDebounce(PDFExportInitialValues.email, 325);
  const [contact, setContact] = useDebounce(PDFExportInitialValues.contact, 325);

  // Configuration
  const [watermark, setWatermark] = useDebounce("", 325);
  const [showDate, setShowDate] = useState(PDFExportInitialValues.showDate);
  const [orientation, setOrientation] = useState(PDFExportInitialValues.orientation);
  const [showPageNumber, setShowPageNumber] = useState(PDFExportInitialValues.showPageNumber);
  const [showWatermark, setShowWatermark] = useState(PDFExportInitialValues.showWatermark);

  // Header
  const handleProjectNameChange = (e: ChangeEvent<HTMLInputElement>) =>
    setProjectName(e.target.value);
  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  // Contact Details
  const handleContactDetailsChange = (
    e: ChangeEvent<HTMLInputElement>,
    detail: "company" | "address" | "phone" | "email" | "contact"
  ) => {
    switch (detail) {
      case "company":
        setCompanyName(e.target.value);
        break;
      case "address":
        setAddress(e.target.value);
        break;
      case "phone":
        setPhone(e.target.value);
        break;
      case "email":
        setEmail(e.target.value);
        break;
      case "contact":
        setContact(e.target.value);
        break;
    }
  };

  // Configuration
  const handleWatermarkChange = (e: ChangeEvent<HTMLInputElement>) => {
    setWatermark(e.target.value);
  };
  const toggleShowDate = () => setShowDate((prevState) => !prevState);
  const toggleOrientation = () => {
    setOrientation((prevState) => (prevState === "portrait" ? "landscape" : "portrait"));
  };
  const toggleShowPageNumber = () => setShowPageNumber((prevState) => !prevState);
  const toggleShowWatermark = () => setShowWatermark((prevState) => !prevState);

  // Download
  const handleDownload = () => {
    const iframe = document.querySelector(".download-iframe");
    const iframeSrc = iframe?.getAttribute("src");

    if (!iframeSrc) return;

    const sanitizedProjectName = sanitizeExportProjectName(project.name);
    const sanitizedTitle = sanitizeExportSegment(title || _title || "export");

    const a = document.createElement("a");
    a.href = iframeSrc;
    a.download = buildExportFilename({
      projectName: sanitizedProjectName,
      exportType: sanitizedTitle,
      extension: "pdf",
    });
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    onDownload?.();
  };

  useEffect(() => {
    setProjectName(project.name || "");
    setTitle(_title || "");
    setWatermark(workspace?.name || "");
  }, [project.name, _title, workspace?.name]);

  const handleClose = () => {
    setProjectName(project.name || "");
    setTitle(_title || "");
    setWatermark(workspace?.name || "");
    setCompanyName(PDFExportInitialValues.companyName);
    setAddress(PDFExportInitialValues.address);
    setPhone(PDFExportInitialValues.phone);
    setEmail(PDFExportInitialValues.email);
    setContact(PDFExportInitialValues.contact);
    setShowDate(PDFExportInitialValues.showDate);
    setOrientation(PDFExportInitialValues.orientation);
    setShowPageNumber(PDFExportInitialValues.showPageNumber);
    setShowWatermark(PDFExportInitialValues.showWatermark);
    onClose();
  };

  const Document = () => (
    <PDFLayout
      showDate={showDate}
      orientation={orientation}
      showPageNumber={showPageNumber}
      watermark={showWatermark ? watermark : ""}
      projectName={projectName}
      title={title}
      companyName={companyName}
      address={address}
      phone={phone}
      email={email}
      contact={contact}
    >
      {children}
    </PDFLayout>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="lg:max-w-screen-xl max-w-full !w-auto m-4 max-h-[calc(100vh-12rem)] overflow-hidden h-full [&>div]:overflow-hidden [&>div]:h-full"
    >
      <ModalHeader title="PDF Export" onClose={handleClose} />
      <ModalBody
        showBottomBorder={false}
        className="max-h-[calc(100vh-24rem)] lg:max-h-full h-full overflow-auto w-max"
        containerClassName="overflow-hidden h-full"
      >
        <div className="flex lg:flex-row flex-col flex-1 pt-4 max-lg:overflow-auto max-lg:items-center overflow-hidden h-full max-lg:gap-4">
          <div className="flex flex-col w-full lg:max-w-[350px] max-lg:max-w-[425px] max-lg:items-center gap-4 justify-between lg:overflow-auto h-full px-4 max-lg:h-fit">
            <div className="flex flex-col w-full gap-4">
              <div className="flex flex-col gap-2">
                <div className="flex flex-row justify-between">
                  <div className="text-strong-1">Header</div>
                </div>
                <div className="px-4 w-full flex flex-col gap-4">
                  <InputV2
                    id="projectName"
                    label="Project Name"
                    onChange={handleProjectNameChange}
                    className="[&>label]:w-full"
                    placeholder={projectName.length ? projectName : "Enter project name"}
                  />
                  <InputV2
                    id="title"
                    label="Title"
                    onChange={handleTitleChange}
                    className="[&>label]:w-full"
                    placeholder={_title?.length ? _title : "Enter title"}
                  />
                </div>
              </div>
              <Divider orientation="horizontal" size="full" />
              <div className="flex flex-col gap-2">
                <div className="flex flex-row justify-between">
                  <div className="text-strong-1">Contact Details</div>
                </div>
                <div className="px-4 w-full flex flex-col gap-2">
                  <InputV2
                    id="companyName"
                    label="Company"
                    onChange={(e) => handleContactDetailsChange(e, "company")}
                    className="[&>label]:w-full"
                  />
                  <InputV2
                    id="address"
                    label="Address"
                    onChange={(e) => handleContactDetailsChange(e, "address")}
                    className="[&>label]:w-full"
                  />
                  <InputV2
                    id="phone"
                    label="Phone"
                    onChange={(e) => handleContactDetailsChange(e, "phone")}
                    className="[&>label]:w-full"
                  />
                  <InputV2
                    id="email"
                    label="Email"
                    onChange={(e) => handleContactDetailsChange(e, "email")}
                    className="[&>label]:w-full"
                  />
                  <InputV2
                    id="contact"
                    label="Contact"
                    onChange={(e) => handleContactDetailsChange(e, "contact")}
                    className="[&>label]:w-full"
                  />
                </div>
              </div>
              <Divider orientation="horizontal" size="full" />
              <div className="flex flex-col gap-2">
                <div className="flex flex-row justify-between">
                  <div className="text-strong-1">Configuration</div>
                </div>
                <div className="px-4 flex flex-col gap-4 w-full">
                  <div className="flex flex-row gap-4 justify-between">
                    <label htmlFor="orientation" className="text-regular-2 text-left opacity-80">
                      Portrait
                    </label>
                    <Switch
                      id="orientation"
                      checked={orientation === "portrait"}
                      onChange={toggleOrientation}
                    />
                  </div>
                  <div className="flex flex-row gap-4 justify-between">
                    <label htmlFor="showDate" className="text-regular-2 text-left opacity-80">
                      Show date
                    </label>
                    <Switch id="showDate" checked={showDate} onChange={toggleShowDate} />
                  </div>
                  <div className="flex flex-row gap-4 justify-between">
                    <label htmlFor="showPageNumber" className="text-regular-2 text-left opacity-80">
                      Show page number
                    </label>
                    <Switch
                      id="showPageNumber"
                      checked={showPageNumber}
                      onChange={toggleShowPageNumber}
                    />
                  </div>
                  <Divider orientation="horizontal" size="full" />
                  <div className="flex flex-row gap-4 justify-between">
                    <label htmlFor="showWatermark" className="text-regular-2 text-left opacity-80">
                      Show watermark
                    </label>
                    <Switch
                      id="showWatermark"
                      checked={showWatermark}
                      onChange={toggleShowWatermark}
                    />
                  </div>
                  <InputV2
                    id="watermark"
                    label="Watermark"
                    onChange={handleWatermarkChange}
                    className="[&>label]:w-full"
                    placeholder={watermark.length ? watermark : "Enter watermark"}
                  />
                </div>
              </div>
            </div>
            <Button onClick={handleDownload}>Download</Button>
          </div>
          <Divider orientation="vertical" size="full" />
          <div className="flex flex-row w-auto justify-center px-4 max-lg:self-start">
            <Document />
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default PDFExport;
